import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Bell, NotebookText, RotateCw, Wallet } from "lucide-react";
import { Logo } from "../../components/laxmi/Chrome";
import { BetSheet, ballClass } from "../../components/game/BetSheet";
import { ChartTable, MyBets, ResultsTable, Rules } from "../../components/game/WinGoTables";
import { Announcement } from "./Home";
import { api, money } from "../../lib/api";
import { useSite } from "../../lib/site";

const GAMES = [["wingo1", "WinGo", "1 Min"]];
const MULTS = [1, 5, 10, 20, 50, 100];
const pad = n => String(n).padStart(2, "0");

function useGameState(game) {
  const [state, setState] = useState(null), [left, setLeft] = useState(0);
  const offset = useRef(0), busy = useRef(false);
  const load = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try { const r = await api.get("/game/state", { params: { game } }); offset.current = new Date(r.data.server_time) - Date.now(); setState(r.data); } catch {}
    busy.current = false;
  }, [game]);
  useEffect(() => { setState(null); load(); const p = setInterval(load, 4000); return () => clearInterval(p); }, [load]);
  useEffect(() => {
    const t = setInterval(() => {
      if (!state?.period) return setLeft(0);
      const s = Math.max(0, Math.ceil((new Date(state.period.end_at) - (Date.now() + offset.current)) / 1000));
      setLeft(s);
      if (s === 0) load();
    }, 250);
    return () => clearInterval(t);
  }, [state, load]);
  return { state, left, load };
}

function TicketCard({ state, left, label, onRules }) {
  const digits = [...pad(Math.floor(left / 60)), ":", ...pad(left % 60)];
  return (
    <section className="ticket" data-testid="period-card">
      <div className="ticket-left"><button className="how-btn" onClick={onRules} data-testid="how-to-play-button"><NotebookText size={16} /> How to play</button>
        <small>{label}</small><div className="last-balls">{state.results.slice(0, 5).map(r => <i key={r.period_id} className={ballClass(r.result_number)}><span>{r.result_number}</span></i>)}</div></div>
      <div className="ticket-right"><small>Time remaining</small>
        {state.period ? <><div className="digits" data-testid="period-countdown">{digits.map((d, i) => <b key={i} className={d === ":" ? "colon" : ""}>{d}</b>)}</div>
          <span className="pid" data-testid="current-period-id">{state.period.period_id}</span></> : <span className="paused" data-testid="game-paused">Game paused</span>}</div>
    </section>
  );
}

function Board({ locked, left, onPick, mult, setMult }) {
  const random = () => { const n = Math.floor(Math.random() * 10); onPick("number", String(n), String(n)); };
  return (
    <section className={`bet-board ${locked ? "is-locked" : ""}`} data-testid="bet-board">
      {locked && left > 0 && <div className="lock-overlay" data-testid="bet-locked"><b>{pad(left % 60)[0]}</b><b>{pad(left % 60)[1]}</b></div>}
      <div className="color-row">{["Green", "Violet", "Red"].map(c => <button key={c} className={`c-${c.toLowerCase()}`} onClick={() => onPick("color", c)} data-testid={`bet-color-${c.toLowerCase()}`}>{c}</button>)}</div>
      <div className="number-grid">{[...Array(10).keys()].map(n => <button key={n} className={ballClass(n)} onClick={() => onPick("number", String(n), String(n))} data-testid={`bet-number-${n}`}><span>{n}</span></button>)}</div>
      <div className="mult-row"><button className="random" onClick={random} data-testid="bet-random-button">Random</button>
        {MULTS.map(m => <button key={m} className={mult === m ? "on" : ""} onClick={() => setMult(m)} data-testid={`board-mult-${m}`}>X{m}</button>)}</div>
      <div className="size-row"><button className="c-big" onClick={() => onPick("size", "Big")} data-testid="bet-size-big">Big</button><button className="c-small" onClick={() => onPick("size", "Small")} data-testid="bet-size-small">Small</button></div>
    </section>
  );
}

export default function WinGo({ data, setPage, onRefresh }) {
  const { site } = useSite();
  const game = "wingo1";
  const [sel, setSel] = useState(null), [tab, setTab] = useState("results"), [mult, setMult] = useState(1), [rules, setRules] = useState(false);
  const { state, left, load } = useGameState(game);
  const lastPeriod = useRef(null);
  const pid = state?.period?.period_id;
  useEffect(() => { if (lastPeriod.current && pid && pid !== lastPeriod.current) onRefresh(); lastPeriod.current = pid; }, [pid, onRefresh]);
  const meta = GAMES.find(g => g[0] === game);
  const label = `${meta[1]} ${meta[2]}`;
  const locked = !state?.period || left <= state.lock_seconds;
  const pick = (type, value, label2) => !locked && setSel({ type, value, label: label2 || value });
  return (
    <div className="screen wingo">
      <header className="page-header wingo-head"><button onClick={() => setPage("home")} data-testid="page-back-button"><ArrowLeft /></button><Logo /><button onClick={() => setPage("notifications")} data-testid="notifications-button"><Bell /></button></header>
      <section className="wallet-card" data-testid="wingo-wallet">
        <div className="wallet-amt"><strong data-testid="wingo-balance">{money(data.wallet.balance)}</strong><button onClick={onRefresh} data-testid="wingo-refresh-balance"><RotateCw size={18} /></button></div>
        <span className="wallet-label"><Wallet size={18} /> Wallet balance</span>
        <div className="wallet-btns"><button className="wd" onClick={() => setPage("withdraw")} data-testid="wingo-withdraw-button">Withdraw</button><button className="dp" onClick={() => setPage("deposit")} data-testid="wingo-deposit-button">Deposit</button></div>
      </section>
      <Announcement text={site.announcement} />
      {!state ? <div className="empty">Loading…</div> : <>
        <TicketCard state={state} left={left} label={label} onRules={() => setRules(true)} />
        <Board locked={locked} left={left} onPick={pick} mult={mult} setMult={setMult} />
        <div className="history-tabs">{[["results", "Game history"], ["chart", "Chart"], ["mine", "My history"]].map(([id, l]) => <button key={id} className={tab === id ? "on" : ""} onClick={() => setTab(id)} data-testid={`wingo-${id}-tab`}>{l}</button>)}</div>
        {tab === "results" ? <ResultsTable results={state.results.slice(0, 10)} /> : tab === "chart" ? <ChartTable results={state.results.slice(0, 10)} /> : <MyBets bets={state.my_bets} />}
      </>}
      {rules && <Rules onClose={() => setRules(false)} />}
      {sel && state?.period && <BetSheet key={`${sel.type}${sel.value}`} sel={sel} game={{ game, label }} period={state.period} balance={data.wallet.balance} initialQty={mult} onClose={() => setSel(null)} onPlaced={() => { setSel(null); load(); onRefresh(); }} />}
    </div>
  );
}
